import React, { useEffect, useRef } from "react";

import { Route, useHistory, useLocation } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { AuthProvider } from "../../contexts/auth";
import { CurrencyProvider } from "../../contexts/currency";
import { PageProvider } from "../../contexts/page";
import { ProfileProvider } from "../../contexts/profile";
import { SeedProvider } from "../../contexts/seed";
import { ToastProvider } from "../../contexts/toast";
import { AuthProvider as ZoAuthProvider } from "../../contexts/zoauth";
import { gtag, updateTrackingInfo } from "../../utils/track";
import Routes from "./components/Routes";
// import FooterNav from "./components/FooterNav";

interface WebsiteProps {}

const US_REDIRECT_KEY = "us_redirect_timestamp";
const REDIRECT_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

// List of states that should redirect to /us/coming-soon (using two-letter codes)
const COMING_SOON_STATES = [
  "CA", // California
  "NY", // New York
  "IL", // Illinois
  "MD", // Maryland
  "MN", // Minnesota
  "WA", // Washington
  "ND", // North Dakota
  "VA", // Virginia
  "RI", // Rhode Island
  "MI", // Michigan
  "IN", // Indiana
  "SD", // South Dakota
  "WI", // Wisconsin
  "HI", // Hawaii
];

const Website: React.FC<WebsiteProps> = () => {
  const location = useLocation();
  const history = useHistory();

  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    updateTrackingInfo(location.search);
  }, [location.search]);

  useEffect(() => {
    const sendPageView = (path: string) => {
      gtag("event", "page_view", {
        page_path: path,
      });
    };

    // Send pageview on initial load
    sendPageView(window.location.pathname);

    // Send pageview on route changes
    const unlisten = history.listen((location) => {
      sendPageView(location.pathname);
    });

    return () => {
      unlisten();
    };
  }, [history]);

  // Add effect to check user's country and redirect if from US
  useEffect(() => {
    const checkUserCountry = async () => {
      try {
        // Check if we have a valid cached redirect
        const cachedRedirect = localStorage.getItem(US_REDIRECT_KEY);
        if (cachedRedirect) {
          const redirectTime = parseInt(cachedRedirect);
          if (Date.now() - redirectTime < REDIRECT_TTL) {
            // If we have a valid cache, don't redirect - let user stay on current page
            return;
          } else {
            // Clear expired cache
            localStorage.removeItem(US_REDIRECT_KEY);
          }
        }

        // If no valid cache, check country using IP
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();

        // If user is from US
        if (data.country_name === "United States") {
          // Store redirect timestamp in localStorage
          localStorage.setItem(US_REDIRECT_KEY, Date.now().toString());

          // Check if user is from a coming-soon state
          if (COMING_SOON_STATES.includes(data.region_code)) {
            // If not already on coming-soon page, redirect to it
            if (!location.pathname.startsWith("/us/coming-soon")) {
              history.push("/us/coming-soon");
            }
          } else {
            // If not already on /us path, redirect to it
            if (!location.pathname.startsWith("/us")) {
              history.push("/us");
            }
          }
        }
      } catch (error) {
        console.error("Error detecting user country:", error);
      }
    };

    checkUserCountry();
  }, [history, location.pathname]);

  return (
    <ToastProvider>
      <ZoAuthProvider>
        <AuthProvider name="website">
          <QueryParamProvider ReactRouterRoute={Route}>
            <ProfileProvider>
              <SeedProvider name="website">
                <CurrencyProvider>
                  <PageProvider>
                    <main
                      className="w-screen min-h-screen bg-white max-w-full text-dark theme-website"
                      ref={ref}
                    >
                      <Routes />
                    </main>
                  </PageProvider>
                </CurrencyProvider>
              </SeedProvider>
            </ProfileProvider>
          </QueryParamProvider>
        </AuthProvider>
      </ZoAuthProvider>
    </ToastProvider>
  );
};

export default Website;
